import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Box,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  SimpleGrid,
  VStack,
  useToast,
  HStack,
  Tag,
  TagLabel,
  TagCloseButton,
  Checkbox,
  Heading,
} from "@chakra-ui/react";
import { createNeed, updateNeed, listUsersInMyBusinessUnit } from "../../../api/needsApi";
import { listClients, listClientContacts } from "../../../api/clientsApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import {
  NEED_STATUSES,
  NEED_STATUS_LABELS,
  NEED_STATUS_LABEL_KEYS,
  getStatusLabel
} from "../../../constants/statuses";
import { ACQUISITION_CHANNELS, CONTRACT_TYPES } from "../../../constants/needs";
import { useTranslation } from "react-i18next";

export const EMPTY_FORM = {
  title: "",
  client_id: "",
  date: "",
  description: "",
  skillsTechnical: [],
  skillsFunctional: [],
  skillsBusiness: [],
  status: "",
  acquisitionChannels: [],
  contractTypes: [],
  asap: false,
  startDate: "",
  endDate: "",
  salesId: "",
  recruiterId: "",
  recruiterIds: [],
  clientContactId: "",
  jobDescriptionUrl: ""
};

const parseMulti = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch (_) {
      // ignore
    }
  }
  return [];
};

const parseSkills = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    } catch (_) {
      // ignore
    }
  }
  return [];
};

const NeedForm = ({ initialData, onSuccess, onCancel, isEdit: isEditProp, isActive = true, showBackButton = false, onBack }) => {
  const { selectedBusinessUnitId } = useBusinessUnit();
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientContacts, setClientContacts] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [skillInput, setSkillInput] = useState({ technical: "", functional: "", business: "" });
  const toast = useToast();
  const { t } = useTranslation("portal");

  const isEdit = isEditProp ?? Boolean(initialData?.id);

  useEffect(() => {
    const today = new Date();
    const formattedDate = today.toISOString().split("T")[0];

    if (initialData) {
      setForm({
        title: initialData?.title || "",
        client_id: initialData?.client_id || initialData?.clientId || "",
        date: initialData?.date || formattedDate,
        description: initialData?.description || "",
        skillsTechnical: parseSkills(
          initialData?.skillsTechnical || initialData?.skills_technical
        ),
        skillsFunctional: parseSkills(
          initialData?.skillsFunctional || initialData?.skills_functional
        ),
        skillsBusiness: parseSkills(
          initialData?.skillsBusiness || initialData?.skills_business
        ),
        status: initialData?.status || "",
        acquisitionChannels: parseMulti(
          initialData?.acquisitionChannels || initialData?.acquisition_channels
        ),
        contractTypes: parseMulti(
          initialData?.contractTypes || initialData?.contract_types
        ),
        asap: initialData?.asap || false,
        startDate: initialData?.startDate || initialData?.start_date || "",
        endDate: initialData?.endDate || initialData?.end_date || "",
        salesId: initialData?.salesId || initialData?.sales_id || "",
        recruiterId: initialData?.recruiterId || initialData?.recruiter_id || "",
        recruiterIds: initialData?.recruiterIds || [],
        clientContactId: initialData?.clientContactId || initialData?.client_contact_id || "",
        jobDescriptionUrl:
          initialData?.jobDescriptionUrl ||
          initialData?.job_description_url ||
          initialData?.jobDescription ||
          ""
      });
    } else {
      setForm({
        ...EMPTY_FORM,
        date: formattedDate
      });
    }
    setSkillInput({ technical: "", functional: "", business: "" });
  }, [initialData, isEdit]);

  useEffect(() => {
    if (!isActive) return;
    Promise.all([
      listUsersInMyBusinessUnit({ buId: selectedBusinessUnitId || undefined }),
      listClients({ page: 1, pageSize: 1000 }, { buId: selectedBusinessUnitId || undefined })
    ])
      .then(([usersRes, clientsRes]) => {
        setUsers(usersRes || []);
        const loadedClients = clientsRes?.items || [];
        setClients(loadedClients);
        // Set search field to selected client name if form or initialData has a client
        const preselectedClientId = initialData?.client_id || initialData?.clientId;
        if (preselectedClientId) {
          const selected = loadedClients.find((c) => String(c.id) === String(preselectedClientId));
          if (selected) setClientSearch(selected.name);
        }
      })
      .catch(() => {
        setUsers([]);
        setClients([]);
      });
  }, [isActive, selectedBusinessUnitId]);

  // Load contacts for a given client ID
  const loadContactsForClient = useCallback((clientId) => {
    if (!clientId) {
      setClientContacts([]);
      return;
    }
    listClientContacts(clientId, { buId: selectedBusinessUnitId || undefined })
      .then((res) => {
        const contacts = Array.isArray(res) ? res : res?.items || [];
        setClientContacts(contacts);
      })
      .catch(() => setClientContacts([]));
  }, [selectedBusinessUnitId]);

  // Load contacts when editing an existing need that already has a client
  useEffect(() => {
    const existingClientId = initialData?.client_id || initialData?.clientId;
    if (existingClientId) {
      loadContactsForClient(existingClientId);
    }
  }, [initialData, loadContactsForClient]);

  const filteredClients = clientSearch.trim()
    ? clients.filter((c) =>
        c.name.toLowerCase().includes(clientSearch.toLowerCase())
      )
    : clients;

  const handleClientSelect = (client) => {
    setForm((prev) => ({ ...prev, client_id: String(client.id), clientContactId: "" }));
    setClientSearch(client.name);
    setShowClientDropdown(false);
    setClientContacts([]);
    loadContactsForClient(client.id);
  };

  const isValid = useMemo(() => {
    const hasRecruiter = (Array.isArray(form.recruiterIds) && form.recruiterIds.length > 0) ||
      String(form.recruiterId).trim().length > 0;
    return (
      form.title.trim().length > 0 &&
      form.client_id &&
      form.description.trim().length > 0 &&
      form.status.trim().length > 0 &&
      String(form.salesId).trim().length > 0 &&
      hasRecruiter
    );
  }, [
    form.title,
    form.client_id,
    form.description,
    form.status,
    form.salesId,
    form.recruiterId,
    form.recruiterIds
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleMultiValue = (field, value) => {
    setForm((prev) => {
      const current = Array.isArray(prev[field]) ? prev[field] : [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [field]: next };
    });
  };

  const handleSkillAdd = (type) => {
    const inputValue = skillInput[type].trim();
    if (!inputValue) return;
    const fieldName =
      type === "technical"
        ? "skillsTechnical"
        : type === "functional"
        ? "skillsFunctional"
        : "skillsBusiness";
    setForm((prev) => ({
      ...prev,
      [fieldName]: [...prev[fieldName], inputValue]
    }));
    setSkillInput((prev) => ({ ...prev, [type]: "" }));
  };

  const handleSkillRemove = (type, index) => {
    const fieldName =
      type === "technical"
        ? "skillsTechnical"
        : type === "functional"
        ? "skillsFunctional"
        : "skillsBusiness";
    setForm((prev) => ({
      ...prev,
      [fieldName]: prev[fieldName].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    setIsSubmitting(true);
    try {
      const today = new Date();
      const dateValue = form.date || today.toISOString().split("T")[0];

      const payload = {
        title: form.title,
        client_id: Number(form.client_id),
        date: dateValue,
        description: form.description || undefined,
        skillsTechnical:
          form.skillsTechnical.length > 0 ? form.skillsTechnical : undefined,
        skillsFunctional:
          form.skillsFunctional.length > 0 ? form.skillsFunctional : undefined,
        skillsBusiness:
          form.skillsBusiness.length > 0 ? form.skillsBusiness : undefined,
        status: form.status || undefined,
        acquisitionChannels:
          Array.isArray(form.acquisitionChannels) &&
          form.acquisitionChannels.length > 0
            ? form.acquisitionChannels
            : undefined,
        contractTypes:
          Array.isArray(form.contractTypes) && form.contractTypes.length > 0
            ? form.contractTypes
            : undefined,
        asap: form.asap ? true : undefined,
        startDate: !form.asap && form.startDate ? form.startDate : undefined,
        endDate: form.endDate || undefined,
        salesId: form.salesId ? Number(form.salesId) : undefined,
        recruiterIds: Array.isArray(form.recruiterIds) && form.recruiterIds.length > 0
          ? form.recruiterIds.map(Number)
          : form.recruiterId ? [Number(form.recruiterId)] : undefined,
        clientContactId: form.clientContactId ? Number(form.clientContactId) : undefined,
        jobDescriptionUrl: form.jobDescriptionUrl || undefined
      };

      if (isEdit) {
        const updated = await updateNeed(initialData.id, payload, {
          buId: selectedBusinessUnitId || undefined
        });
        toast({ title: t("pilotNeeds.toasts.updated"), status: "success" });
        onSuccess?.(updated);
      } else {
        const created = await createNeed(payload, {
          buId: selectedBusinessUnitId || undefined
        });
        toast({ title: t("pilotNeeds.toasts.created"), status: "success" });
        onSuccess?.(created);
      }
      onCancel?.();
    } catch (e) {
      toast({
        title: t("pilotNeeds.toasts.operationFailedTitle"),
        description: e?.response?.data?.message || "Please try again",
        status: "error"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <VStack align="stretch" spacing={6}>
        <Box>
          <Heading size="sm" mb={3}>
            {t("pilotNeeds.form.sections.general")}
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl isRequired>
              <FormLabel>{t("pilotNeeds.form.fields.title")}</FormLabel>
              <Input
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder={t("pilotNeeds.form.placeholders.title")}
              />
            </FormControl>
            <FormControl isRequired position="relative">
              <FormLabel>{t("pilotNeeds.form.fields.client")}</FormLabel>
              <Input
                placeholder={t("pilotNeeds.form.placeholders.searchClient", { defaultValue: "Search for a company..." })}
                value={clientSearch}
                onChange={(e) => {
                  setClientSearch(e.target.value);
                  setShowClientDropdown(true);
                  if (!e.target.value.trim()) {
                    setForm((prev) => ({ ...prev, client_id: "", clientContactId: "" }));
                    setClientContacts([]);
                  }
                }}
                onFocus={() => setShowClientDropdown(true)}
                onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
                autoComplete="off"
              />
              {showClientDropdown && filteredClients.length > 0 && (
                <Box
                  position="absolute"
                  zIndex={10}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  mt={1}
                  maxH="200px"
                  overflowY="auto"
                  w="100%"
                  boxShadow="md"
                >
                  {filteredClients.slice(0, 50).map((c) => (
                    <Box
                      key={c.id}
                      px={3}
                      py={2}
                      cursor="pointer"
                      bg={String(c.id) === String(form.client_id) ? "blue.50" : "white"}
                      _hover={{ bg: "gray.100" }}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleClientSelect(c)}
                      fontSize="sm"
                    >
                      {c.name}
                      {c.sector ? ` — ${c.sector}` : ""}
                    </Box>
                  ))}
                </Box>
              )}
              {showClientDropdown && clientSearch.trim() && filteredClients.length === 0 && (
                <Box
                  position="absolute"
                  zIndex={10}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  mt={1}
                  px={3}
                  py={2}
                  w="100%"
                  boxShadow="md"
                  fontSize="sm"
                  color="gray.500"
                >
                  {t("pilotNeeds.form.placeholders.noCompaniesFound", { defaultValue: "No companies found" })}
                </Box>
              )}
            </FormControl>
            <FormControl isRequired>
              <FormLabel>{t("pilotNeeds.form.fields.status")}</FormLabel>
              <Select
                name="status"
                value={form.status}
                onChange={handleChange}
                placeholder={t("pilotNeeds.form.fields.status")}
                sx={{
                  paddingTop: "0 !important",
                  paddingBottom: "0 !important",
                  lineHeight: "32px !important",
                  height: "32px !important"
                }}
                paddingTop="0"
                paddingBottom="0"
              >
                {NEED_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {getStatusLabel(
                      t,
                      NEED_STATUS_LABEL_KEYS,
                      NEED_STATUS_LABELS,
                      s.value
                    )}
                  </option>
                ))}
              </Select>
            </FormControl>
          </SimpleGrid>
          <FormControl mt={4} isRequired>
            <FormLabel>{t("pilotNeeds.form.fields.description")}</FormLabel>
            <Textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder={t("pilotNeeds.form.placeholders.description")}
              rows={4}
            />
          </FormControl>
        </Box>

        <Divider />

        <Box>
          <Heading size="sm" mb={3}>
            {t("pilotNeeds.form.sections.people")}
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl isRequired>
              <FormLabel>{t("pilotNeeds.form.fields.sales")}</FormLabel>
              <Select
                name="salesId"
                value={form.salesId || ""}
                onChange={handleChange}
                placeholder={t("pilotNeeds.form.fields.sales")}
                sx={{
                  paddingTop: "0 !important",
                  paddingBottom: "0 !important",
                  lineHeight: "32px !important",
                  height: "32px !important"
                }}
                paddingTop="0"
                paddingBottom="0"
              >
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} - {u.email}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>{t("pilotNeeds.form.fields.recruiter", { defaultValue: "Recruiter(s)" })}</FormLabel>
              <Box
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                p={2}
                maxH="140px"
                overflowY="auto"
              >
                {users.map((u) => (
                  <Checkbox
                    key={u.id}
                    isChecked={
                      (Array.isArray(form.recruiterIds) && form.recruiterIds.includes(String(u.id))) ||
                      (Array.isArray(form.recruiterIds) && form.recruiterIds.includes(u.id))
                    }
                    onChange={(e) => {
                      const uid = String(u.id);
                      setForm((prev) => {
                        const current = Array.isArray(prev.recruiterIds) ? prev.recruiterIds.map(String) : [];
                        const next = e.target.checked
                          ? [...current, uid]
                          : current.filter((id) => id !== uid);
                        return { ...prev, recruiterIds: next, recruiterId: next[0] || "" };
                      });
                    }}
                    display="block"
                    mb={1}
                  >
                    {u.first_name} {u.last_name}
                  </Checkbox>
                ))}
              </Box>
            </FormControl>
          </SimpleGrid>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={4}>
            <FormControl>
              <FormLabel>{t("pilotNeeds.form.fields.contactPerson", { defaultValue: "Contact Person" })}</FormLabel>
              <Select
                name="clientContactId"
                value={form.clientContactId || ""}
                onChange={handleChange}
                placeholder={t("pilotNeeds.form.placeholders.contactPerson", { defaultValue: "Select a contact..." })}
                isDisabled={!form.client_id}
                sx={{
                  paddingTop: "0 !important",
                  paddingBottom: "0 !important",
                  lineHeight: "32px !important",
                  height: "32px !important"
                }}
                paddingTop="0"
                paddingBottom="0"
              >
                {clientContacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.role ? ` — ${c.role}` : ""}
                  </option>
                ))}
              </Select>
            </FormControl>
          </SimpleGrid>
        </Box>

        <Divider />

        <Box>
          <Heading size="sm" mb={3}>
            {t("pilotNeeds.form.sections.dates")}
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl>
              <FormLabel>{t("pilotNeeds.form.fields.startDate")}</FormLabel>
              <Checkbox
                isChecked={form.asap}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    asap: e.target.checked,
                    startDate: e.target.checked ? "" : prev.startDate
                  }))
                }
                mb={2}
                sx={{
                  paddingTop: "0 !important",
                  paddingBottom: "0 !important",
                  lineHeight: "32px !important",
                  height: "32px !important"
                }}
              >
                {t("pilotNeeds.form.fields.asap")}
              </Checkbox>
              {!form.asap && (
                <Input
                  type="date"
                  name="startDate"
                  value={form.startDate}
                  onChange={handleChange}
                />
              )}
            </FormControl>
            <FormControl>
              <FormLabel>{t("pilotNeeds.form.fields.endDate")}</FormLabel>
              <Input
                type="date"
                name="endDate"
                value={form.endDate}
                onChange={handleChange}
              />
            </FormControl>
          </SimpleGrid>
        </Box>

        <Divider />

        <Box>
          <Heading size="sm" mb={3}>
            {t("pilotNeeds.form.sections.channels")}
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl>
              <FormLabel>
                {t("pilotNeeds.form.fields.acquisitionChannels")}
              </FormLabel>
              <HStack spacing={4} wrap="wrap">
                {ACQUISITION_CHANNELS.map((opt) => (
                  <Checkbox
                    key={opt.value}
                    isChecked={form.acquisitionChannels.includes(opt.value)}
                    onChange={() =>
                      toggleMultiValue("acquisitionChannels", opt.value)
                    }
                  >
                    {opt.label}
                  </Checkbox>
                ))}
              </HStack>
            </FormControl>
            <FormControl>
              <FormLabel>{t("pilotNeeds.form.fields.contractTypes")}</FormLabel>
              <HStack spacing={4} wrap="wrap">
                {CONTRACT_TYPES.map((opt) => (
                  <Checkbox
                    key={opt.value}
                    isChecked={form.contractTypes.includes(opt.value)}
                    onChange={() =>
                      toggleMultiValue("contractTypes", opt.value)
                    }
                  >
                    {opt.label}
                  </Checkbox>
                ))}
              </HStack>
            </FormControl>
          </SimpleGrid>
        </Box>

        <Divider />

        <Box>
          <Heading size="sm" mb={3}>
            {t("pilotNeeds.form.sections.skills")}
          </Heading>
          <VStack align="stretch" spacing={4}>
            <FormControl>
              <FormLabel>
                {t("pilotNeeds.form.fields.technicalSkills")}
              </FormLabel>
              <HStack mb={2}>
                <Input
                  placeholder={t("pilotNeeds.form.placeholders.technicalSkill")}
                  value={skillInput.technical}
                  onChange={(e) =>
                    setSkillInput((prev) => ({ ...prev, technical: e.target.value }))
                  }
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSkillAdd("technical");
                    }
                  }}
                />
                <Button onClick={() => handleSkillAdd("technical")} size="sm">
                  {t("pilotNeeds.form.buttons.addSkill")}
                </Button>
              </HStack>
              <HStack wrap="wrap" spacing={2}>
                {form.skillsTechnical.map((skill, idx) => (
                  <Tag key={idx} size="md" variant="solid" colorScheme="purple">
                    <TagLabel>{skill}</TagLabel>
                    <TagCloseButton
                      onClick={() => handleSkillRemove("technical", idx)}
                    />
                  </Tag>
                ))}
              </HStack>
            </FormControl>

            <FormControl>
              <FormLabel>
                {t("pilotNeeds.form.fields.functionalSkills")}
              </FormLabel>
              <HStack mb={2}>
                <Input
                  placeholder={t(
                    "pilotNeeds.form.placeholders.functionalSkill"
                  )}
                  value={skillInput.functional}
                  onChange={(e) =>
                    setSkillInput((prev) => ({
                      ...prev,
                      functional: e.target.value
                    }))
                  }
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSkillAdd("functional");
                    }
                  }}
                />
                <Button onClick={() => handleSkillAdd("functional")} size="sm">
                  {t("pilotNeeds.form.buttons.addSkill")}
                </Button>
              </HStack>
              <HStack wrap="wrap" spacing={2}>
                {form.skillsFunctional.map((skill, idx) => (
                  <Tag key={idx} size="md" variant="solid" colorScheme="orange">
                    <TagLabel>{skill}</TagLabel>
                    <TagCloseButton
                      onClick={() => handleSkillRemove("functional", idx)}
                    />
                  </Tag>
                ))}
              </HStack>
            </FormControl>

            <FormControl>
              <FormLabel>
                {t("pilotNeeds.form.fields.businessSkills")}
              </FormLabel>
              <HStack mb={2}>
                <Input
                  placeholder={t(
                    "pilotNeeds.form.placeholders.businessSkill"
                  )}
                  value={skillInput.business}
                  onChange={(e) =>
                    setSkillInput((prev) => ({ ...prev, business: e.target.value }))
                  }
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSkillAdd("business");
                    }
                  }}
                />
                <Button onClick={() => handleSkillAdd("business")} size="sm">
                  {t("pilotNeeds.form.buttons.addSkill")}
                </Button>
              </HStack>
              <HStack wrap="wrap" spacing={2}>
                {form.skillsBusiness.map((skill, idx) => (
                  <Tag key={idx} size="md" variant="solid" colorScheme="green">
                    <TagLabel>{skill}</TagLabel>
                    <TagCloseButton
                      onClick={() => handleSkillRemove("business", idx)}
                    />
                  </Tag>
                ))}
              </HStack>
            </FormControl>
          </VStack>
        </Box>
      </VStack>

      <HStack justify="flex-end" mt={4} spacing={3}>
        {showBackButton && onBack && (
          <Button variant="ghost" onClick={onBack}>
            {t("pilotNeeds.createWizard.buttons.back")}
          </Button>
        )}
        <Button variant="ghost" onClick={onCancel}>
          {t("pilotNeeds.form.buttons.cancel")}
        </Button>
        <Button
          colorScheme="teal"
          onClick={handleSubmit}
          isDisabled={!isValid}
          isLoading={isSubmitting}
        >
          {isEdit
            ? t("pilotNeeds.form.buttons.saveChanges")
            : t("pilotNeeds.form.buttons.create")}
        </Button>
      </HStack>
    </>
  );
};

export default NeedForm;
